import { Image as ImageType } from '@commerce/types/common'
import { SubscribeForm } from '@components/common'
import { linkResolver } from '@lib/prismic'
import cn from 'classnames'
import Image from 'next/legacy/image'
import { RichText, RichTextBlock } from 'prismic-reactjs'
import { FC, useEffect, useRef, useState } from 'react'

interface props {
  primary: {
    background_colour: string
    confirmation_message: RichTextBlock[]
    description: RichTextBlock[]
    fine_print: RichTextBlock[]
    feature_image: ImageType
    heading: string
    subtitle: string
    klaviyo_list_id: string
    list_source: string
    padding_top: string
    padding_top_mobile: string
    success_description: RichTextBlock[]
    success_heading: string
    success_subtitle: string
    collect_mobile_number: boolean
  }
}

const Newsletter: FC<props> = ({ primary }) => {
  const {
    background_colour,
    heading,
    subtitle,
    description,
    fine_print,
    feature_image,
    klaviyo_list_id,
    list_source,
    padding_top,
    padding_top_mobile,
    success_description,
    success_heading,
    success_subtitle,
    collect_mobile_number,
  } = primary

  const [submissionSuccess, setSubmissionSuccess] = useState(false)
  const sectionRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const section = sectionRef.current
    if (section && submissionSuccess && window.innerWidth < 768) {
      window.scrollTo({ top: section.offsetTop - 72, left: 0, behavior: 'smooth' })
    }
  }, [submissionSuccess])

  return (
    <>
      <section
        ref={sectionRef}
        className={cn(
          `newsletter-block pb-48 tablet:pb-80 ${
            padding_top_mobile ? `pt-${padding_top_mobile}` : `pt-${padding_top}`
          } ${padding_top ? `tablet:pt-${padding_top}` : ''}`
        )}
      >
        <div className="wrapper grid grid-cols-8 tablet:grid-cols-12 gap-x-12 gap-y-40 tablet:gap-x-16 tablet:gap-y-0">
          <div
            className={`newsletter-block__col-1 col-span-8 tablet:col-span-4 ${
              feature_image.url ? 'tablet:col-start-2' : 'tablet:col-start-5'
            }`}
          >
            <div className="newsletter-block__content">
              {subtitle && (
                <div className="title mb-8 text-moss">{!submissionSuccess ? subtitle : success_subtitle}</div>
              )}
              {heading && <h5 className="heading-5 mb-20">{!submissionSuccess ? heading : success_heading}</h5>}
              {description[0].text && (
                <div className={`newsletter-block__description body ${!submissionSuccess ? 'mb-28 tablet:mb-40' : ''}`}>
                  <RichText
                    render={!submissionSuccess ? description : success_description}
                    linkResolver={linkResolver}
                  />
                </div>
              )}
              {!submissionSuccess && (
                <SubscribeForm
                  listID={klaviyo_list_id}
                  listSource={list_source}
                  style="column"
                  showError={true}
                  setSubmissionSuccess={setSubmissionSuccess}
                  collect_mobile_number={collect_mobile_number}
                />
              )}
              {!submissionSuccess && fine_print[0].text && (
                <div className="newsletter-block__fine-print text-charcoal body-small mt-12">
                  <RichText render={fine_print} linkResolver={linkResolver} />
                </div>
              )}
            </div>
          </div>
          {feature_image.url && (
            <div className="newsletter-block__image relative col-span-8 tablet:col-start-7 tablet:col-span-6">
              <Image
                src={feature_image.url}
                layout="fill"
                objectFit="cover"
                alt={feature_image.altText || 'Sheet Society'}
              />
            </div>
          )}
        </div>
      </section>
      <style jsx global>
        {`
          ${background_colour
            ? `.newsletter-block {
             background-color: ${background_colour};
          }`
            : ''}

          .newsletter-block__col-1 {
            display: flex;
            align-items: center;
          }

          .newsletter-block__description p:not(:last-child) {
            margin-bottom: 1rem;
          }

          .newsletter-block__image {
            padding-top: 100%;
          }

          .newsletter-block__fine-print a {
            color: #000;
          }

          @media screen and(min-width: 768px) {
            .newsletter-block__image {
              padding-top: 100%;
            }
          }
        `}
      </style>
    </>
  )
}

export default Newsletter
